import { add, differenceInCalendarDays } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { FilteredPlannerViewData } from '~/routes/calendar._index';
import {
  DRAG_HOVER_EXTRA_HEIGHT,
  EXPANDED_BOTTOM_PADDING,
} from './usePlannerDimensions';
import { createCellKey } from './usePlannerEvents';

// Fixed collapsed row heights based on visibility settings
const COLLAPSED_HEIGHT_WITH_BOOKINGS_AND_TIMES = 61;
const COLLAPSED_HEIGHT_WITH_BOOKINGS_ONLY = 40;
const COLLAPSED_HEIGHT_WITH_TIMES_ONLY = 52;
const COLLAPSED_HEIGHT_WITHOUT_BOOKINGS_OR_TIMES = 31;

// Additional height for collapsed rows with multi-day jobs (expand pill + margin)
const MULTI_DAY_JOB_HEIGHT_ADDITION = 25;

interface UseRowHeightsOptions {
  data: FilteredPlannerViewData;
  expandedSiteIds: string[];
  dragHoverSiteId: string | null;
  draggedSiteId: string | null;
  draftCellId: string | null;
  bookingPillHeight: number;
  jobPillMargin: number;
  jobPillHeight: number;
  minRowHeight: number;
  siteMultiDayJobsMap: Map<string, boolean>;
  siteJobCountsMap: Map<string, number[]>;
  start: Date;
  eventsByCell: Map<string, Array<{ event: { status: string } }>>;
  bookingsHiddenEffective: boolean;
  jobDetailEffective: boolean;
}

type RowHeightOptions = {
  expanded: boolean;
  isBeingDraggedOver?: boolean;
  isDraggingFromSite?: boolean;
  isCreatingJobInSite?: boolean;
  hasExistingJobsInCreationCell?: boolean;
};

/**
 * Custom hook that computes row heights for the planner grid.
 * Provides both stable cached heights for the virtualizer and dynamic heights for rendering.
 *
 * Performance: Uses eventsByCell (Map) instead of events array to avoid recalculating
 * on every scroll. This provides O(1) lookup and stable memoization.
 */
export function useRowHeights({
  data,
  expandedSiteIds,
  dragHoverSiteId,
  draggedSiteId,
  draftCellId,
  bookingPillHeight,
  jobPillMargin,
  jobPillHeight,
  minRowHeight,
  siteMultiDayJobsMap,
  siteJobCountsMap,
  start,
  eventsByCell,
  bookingsHiddenEffective,
  jobDetailEffective,
}: UseRowHeightsOptions) {
  // Convert expandedSiteIds to Set for O(1) lookups instead of O(n) Array.includes()
  // This is called for every site when computing siteHeights, so the optimization matters
  const expandedSiteIdsSet = useMemo(
    () => new Set(expandedSiteIds),
    [expandedSiteIds]
  );

  // Fixed collapsed row heights based on bookings/times visibility
  // These are constant regardless of content (multi-day jobs, etc.)
  const collapsedRowHeight = useMemo(() => {
    if (!bookingsHiddenEffective && jobDetailEffective) {
      // Showing both bookings and times
      return COLLAPSED_HEIGHT_WITH_BOOKINGS_AND_TIMES;
    } else if (!bookingsHiddenEffective && !jobDetailEffective) {
      // Showing bookings but not times
      return COLLAPSED_HEIGHT_WITH_BOOKINGS_ONLY;
    } else if (bookingsHiddenEffective && jobDetailEffective) {
      // Showing times only (bookings hidden)
      return COLLAPSED_HEIGHT_WITH_TIMES_ONLY;
    } else {
      // Not showing either bookings or times
      return COLLAPSED_HEIGHT_WITHOUT_BOOKINGS_OR_TIMES;
    }
  }, [bookingsHiddenEffective, jobDetailEffective]);

  // Single source of truth for row heights
  const computeRowHeight = useCallback(
    (siteId: string, options: RowHeightOptions): number => {
      const {
        expanded,
        isBeingDraggedOver = false,
        isDraggingFromSite = false,
        isCreatingJobInSite = false,
        hasExistingJobsInCreationCell = false,
      } = options;

      if (!expanded) {
        // Use fixed collapsed height based on bookings/times settings
        let height = collapsedRowHeight;

        // Add height for multi-day jobs (expand pill indicator)
        const hasMultiDayJobs = siteMultiDayJobsMap.get(siteId) || false;
        if (hasMultiDayJobs) height += MULTI_DAY_JOB_HEIGHT_ADDITION;

        // Add extra height for temporary interactive states
        if (isDraggingFromSite) height += jobPillHeight + jobPillMargin;
        if (isCreatingJobInSite && hasExistingJobsInCreationCell)
          height += jobPillHeight + jobPillMargin + 10;
        if (isBeingDraggedOver) height += DRAG_HOVER_EXTRA_HEIGHT;
        return height;
      }

      const siteIndex = data.findIndex((s) => s.id === siteId);
      if (siteIndex < 0) return minRowHeight;

      const jobCounts = siteJobCountsMap.get(siteId) || [];
      const filteredJobCounts = jobCounts.filter(
        (count): count is number => count !== undefined
      );
      const maxJobCount =
        filteredJobCounts.length > 0 ? Math.max(...filteredJobCounts) : 0;
      const validMaxJobCount =
        isFinite(maxJobCount) && maxJobCount > 0 ? maxJobCount : 1;

      let height =
        bookingPillHeight +
        jobPillMargin +
        validMaxJobCount * (jobPillHeight + jobPillMargin) +
        EXPANDED_BOTTOM_PADDING;
      if (isDraggingFromSite) height += jobPillHeight + jobPillMargin;
      if (isCreatingJobInSite && hasExistingJobsInCreationCell)
        height += jobPillHeight + jobPillMargin + 10;
      if (isBeingDraggedOver) height += DRAG_HOVER_EXTRA_HEIGHT;
      return height;
    },
    [
      collapsedRowHeight,
      siteMultiDayJobsMap,
      bookingPillHeight,
      jobPillHeight,
      jobPillMargin,
      data,
      siteJobCountsMap,
      minRowHeight,
    ]
  );

  // Dynamic height function for live rendering
  const uniformHeight = useCallback(
    (siteId: string) => {
      const isExpanded = expandedSiteIdsSet.has(siteId);
      const isBeingDraggedOver = dragHoverSiteId === siteId;

      const isCreatingJobInSite = !!(
        draftCellId && draftCellId.startsWith(`${siteId}:`)
      );
      let hasExistingJobsInCreationCell = false;

      if (isCreatingJobInSite) {
        const cellDate = draftCellId.split(':')[1];
        if (cellDate) {
          const parsedDate = new Date(cellDate);
          // Guard against invalid date format to prevent NaN propagation
          if (isNaN(parsedDate.getTime())) {
            return computeRowHeight(siteId, {
              expanded: isExpanded,
              isBeingDraggedOver,
              isDraggingFromSite: draggedSiteId === siteId,
              isCreatingJobInSite: false,
              hasExistingJobsInCreationCell: false,
            });
          }
          const cellDateForCheck = add(start, {
            days: differenceInCalendarDays(parsedDate, start),
          });
          const cellKey = createCellKey(siteId, cellDateForCheck);

          // Use O(1) lookup from eventsByCell instead of filtering all events
          const cellEvents = eventsByCell.get(cellKey) || [];
          const existingJobsInCell = cellEvents.filter(
            ({ event }) => event.status !== 'Booking'
          );
          hasExistingJobsInCreationCell = existingJobsInCell.length > 0;
        }
      }

      return computeRowHeight(siteId, {
        expanded: isExpanded,
        isBeingDraggedOver,
        isDraggingFromSite: draggedSiteId === siteId,
        isCreatingJobInSite: Boolean(isCreatingJobInSite),
        hasExistingJobsInCreationCell,
      });
    },
    [
      expandedSiteIdsSet,
      dragHoverSiteId,
      draggedSiteId,
      draftCellId,
      eventsByCell,
      start,
      computeRowHeight,
    ]
  );

  // Stable height cache for virtualizer
  const heightCache = useMemo(() => {
    const cache = new Map<string, number>();

    data.forEach((site) => {
      const isExpanded = expandedSiteIdsSet.has(site.id);
      const isBeingDraggedOver = dragHoverSiteId === site.id;

      if (isExpanded) {
        cache.set(
          site.id,
          computeRowHeight(site.id, { expanded: true, isBeingDraggedOver })
        );
      } else if (isBeingDraggedOver) {
        cache.set(
          site.id,
          computeRowHeight(site.id, {
            expanded: false,
            isBeingDraggedOver: true,
          })
        );
      }
    });

    return cache;
  }, [expandedSiteIdsSet, dragHoverSiteId, data, computeRowHeight]);

  // Virtualizer's estimateSize function
  const estimateRowSize = useCallback(
    (index: number) => {
      const siteId = data[index]?.id;
      if (!siteId) return minRowHeight;

      const cachedHeight = heightCache.get(siteId);
      if (cachedHeight !== undefined) return cachedHeight;

      return computeRowHeight(siteId, { expanded: false });
    },
    [data, heightCache, computeRowHeight, minRowHeight]
  );

  // Precompute row heights and prefix sums for O(1) siteTop
  const siteHeights = useMemo(
    () => data.map((s) => uniformHeight(s.id)),
    [data, uniformHeight]
  );

  const siteTopPrefix = useMemo(() => {
    const out = new Array(siteHeights.length).fill(0);
    for (let i = 1; i < siteHeights.length; i++) {
      out[i] = out[i - 1] + siteHeights[i - 1];
    }
    return out;
  }, [siteHeights]);

  return {
    computeRowHeight,
    uniformHeight,
    heightCache,
    estimateRowSize,
    siteHeights,
    siteTopPrefix,
  };
}
